import * as api from '@gitbook/api-client';
import { type SiteFeatureKey, SiteType } from '@gitbook/api-client';
import { filterOutNullable } from '@gitbook/util-ts';

import { AIFeature } from './ai';
import { SITE_PRICING_PRODUCTS, SitePlanTarget } from './table_sites';

/**
 * Value associated to a site feature.
 */
type SiteFeatureValue = number | boolean;

/**
 * Mapping of site types to their site feature.
 */
type SiteFeaturesByProduct = {
    [key in SiteType]: SiteFeatureValue;
};

/**
 * Complete definition for a site feature object.
 */
export type SiteFeatureDefinition = {
    key: SiteFeatureKey;

    // Organization features this one requires, transitively: access checks and upgrade prompts
    // cover them too, e.g. the site style guide needs AI Lens, which needs AI.
    dependsOn?: readonly Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>[];

    // AI setting of the org that turns this feature off when disabled, regardless of the plan,
    // e.g. turning off the AI assistant disables AI search even on an Ultimate site.
    aiFeature?: AIFeature;

    /** Defaults to disabling the feature when the downgraded plan no longer includes it. */
    downgradeStrategy?: 'disable' | 'unpublish';

    // Generic name of the feature, e.g. `Unlimited traffic`
    title: string;

    // Tooltip label
    tooltipLabel: string;

    // Copy used in the upgrade dialog
    upgradeTitle?: string;
    upgradeDescription?: string;

    // Whether this feature is visible in the org settings/plan section
    unlistedInPlansTable?: boolean;

    // Default true. Whether this feature is visible in plan cards (e.g. Change Plan dialog)
    listedInPlanCards?: boolean;
} & SiteFeaturesByProduct;

/**
 * Dynamic site feature table.
 */
const SITE_FEATURES_INPUT = [
    //
    // Usage
    //
    {
        key: api.SiteFeatureKey.SitesVisitors,
        title: 'Unlimited traffic',
        tooltipLabel: 'We never limit the amount of traffic to your published documentation.',
        upgradeTitle: 'Enjoy unlimited traffic',
        upgradeDescription: 'Remove all traffic limits and grow your audience without constraints.',
        listedInPlanCards: false,

        [SiteType.Basic]: Number.POSITIVE_INFINITY,
        [SiteType.Sponsored]: Number.POSITIVE_INFINITY,
        [SiteType.Premium]: Number.POSITIVE_INFINITY,
        [SiteType.Ultimate]: Number.POSITIVE_INFINITY,
    },

    //
    // Features
    //
    {
        key: api.SiteFeatureKey.SitesPageTrafficInsights,
        title: 'Page view insights',
        tooltipLabel:
            'See which pages your users are viewing the most and how your site traffic compares to the previous week.',
        upgradeTitle: 'Boost your site insights',
        upgradeDescription:
            'Gain deeper insights into page performance to optimize your content strategy.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesPreviewDeployments,
        title: 'Preview deployments',
        tooltipLabel:
            'See your content edits in context with unlimited full site previews before you deploy.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesFullTextSearch,
        title: 'Full text search',
        tooltipLabel:
            'Let users search for keywords and phrases across your docs, with results that take them right to the correct page section.',
        upgradeTitle: 'Enhance search capabilities',
        upgradeDescription: 'Improve visitor experience with powerful full-text search features.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesApiPlayground,
        title: 'API playground',
        tooltipLabel: 'Let users test API endpoints directly in your docs using interactive blocks',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesUserContribution,
        title: 'User contribution',
        tooltipLabel:
            'Allow your users to propose updates and improvements to your docs — enabled via Git Sync.',
        upgradeTitle: 'Get user feedback easily',
        upgradeDescription:
            'Allow your users to propose updates and improvements to your docs — enabled via Git Sync.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesSeo,
        title: 'Built-in SEO',
        tooltipLabel:
            'GitBook handles the meta information, sitemap, responsive design and more for your site, so you don’t have to worry.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesRedirects,
        title: 'Redirect management',
        upgradeTitle: 'Easily set up custom redirects',
        tooltipLabel:
            'Easily set up custom redirects when you migrate your content into GitBook to avoid broken links.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAdaptiveContent,
        title: 'Adaptive content',
        tooltipLabel: 'Show or hide content to specific users based on criteria you specify.',
        upgradeTitle: 'Tailor your content to meet every visitor’s needs',
        upgradeDescription:
            'Decide who sees what and when. Use your own customer data to set powerful access rules and choose which content to show or hide based on criteria.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAiSearch,
        aiFeature: AIFeature.Assistant,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'AI search',
        tooltipLabel:
            'Allow site visitors to ask GitBook AI questions about the content on your site and get summarized answers in seconds.',
        upgradeTitle: 'Unlock AI-powered instant answers',
        upgradeDescription:
            'Allow all site visitors to ask GitBook AI any question and return back simple, semantic answers — with clickable references if they want to dive deeper.',

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAiAssistant,
        aiFeature: AIFeature.Assistant,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'AI Assistant',
        tooltipLabel:
            'Give your visitors a full AI Assistant that can leverage third-party tools to answer questions.',
        upgradeTitle: 'Unlock the AI Assistant',
        upgradeDescription:
            'Provide an interactive assistant connected to your tools for deeper answers.',

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAiInsights,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'AI insights',
        tooltipLabel:
            'Learn what your visitors are asking, how well your docs help them get answers, and where you can improve coverage.',
        upgradeTitle: 'Unlock AI insights',
        upgradeDescription: 'Get a complete view of the Assistant’s performance',

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesConnections,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'Connections',
        tooltipLabel:
            'Connect external data sources so GitBook Assistant can answer questions with more context.',
        upgradeTitle: 'Unlock connections',
        upgradeDescription:
            'Connect external data sources so GitBook Assistant can answer questions using knowledge beyond your docs.',
        unlistedInPlansTable: true,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesBrokenLinks,
        title: 'Broken links',
        tooltipLabel: 'See the URLs visitors hit on your site that no longer exist.',
        upgradeTitle: 'Find your broken links',
        upgradeDescription:
            'See the URLs your visitors land on that no longer exist, so you can redirect them.',
        // Hidden from pricing surfaces until the suggested-redirects rollout completes.
        unlistedInPlansTable: true,
        listedInPlanCards: false,

        // Keep the existing Broken URLs report entitlement; suggested findings roll out separately.
        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesContentGaps,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'Content gaps',
        tooltipLabel:
            'See where your docs fall short, based on what readers ask and report in your connected tools.',
        upgradeTitle: 'Unlock content gaps',
        upgradeDescription:
            'Find the questions your docs don’t answer, based on what readers ask and report in your connected tools.',
        unlistedInPlansTable: true,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: false,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesStyleGuide,
        dependsOn: [api.OrganizationFeature.AiLens],
        title: 'Style guide',
        tooltipLabel: 'Keep your documentation consistent with your team’s writing rules.',
        upgradeTitle: 'Unlock style guides',
        upgradeDescription:
            'Give GitBook Agent your writing rules to keep new and existing documentation consistent.',
        unlistedInPlansTable: true,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: false,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesFixBrokenLinks,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'Fix broken links',
        tooltipLabel:
            'Proactively detect links that 404 for your visitors and fix them with suggested redirects.',
        upgradeTitle: 'Unlock fixing broken links',
        upgradeDescription:
            'Proactively find URLs that break for your visitors, with suggested redirects to fix them.',
        unlistedInPlansTable: true,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: false,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesChannels,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'Channels',
        tooltipLabel:
            'Bring GitBook Agent into external channels so users can ask questions and request changes where they already work.',
        upgradeTitle: 'Unlock channels',
        upgradeDescription:
            'Connect GitBook Agent to external channels so users can ask questions and request changes without leaving their existing workflows.',
        unlistedInPlansTable: true,
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: false,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesEmbed,
        title: 'Docs Embed',
        tooltipLabel: 'Embed your documentation in your product or website using the Docs Embed.',
        upgradeTitle: 'Unlock the Docs Embed',
        upgradeDescription:
            'Embed your documentation in your product or website using the Docs Embed.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAdvancedInsights,
        title: 'Advanced insights',
        tooltipLabel:
            'See which pages your users are viewing the most and how your site traffic compares to the previous week.',
        upgradeTitle: 'Boost your site insights',
        upgradeDescription:
            'Gain deeper insights into yoru site performance to optimize your content strategy.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesCustomDomain,
        downgradeStrategy: 'unpublish',
        title: 'Custom domain',
        tooltipLabel:
            'Publish your content with the URL of your choice (e.g. docs.yourcompany.com).',
        upgradeTitle: 'Use your own domain',
        upgradeDescription:
            'Make your docs site more reachable and recognizable with any custom domain you own.',

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesNoCustomDomain,
        title: 'gitbook.io domain only',
        tooltipLabel: 'Publish your site under the gitbook.io domain.',
        unlistedInPlansTable: true,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: false,
    },

    {
        key: api.SiteFeatureKey.SitesCustomSubdirectory,
        downgradeStrategy: 'unpublish',
        title: 'Custom subdirectory',
        tooltipLabel: 'Publish your content on a subdirectory (e.g. yourcompany.com/docs).',
        upgradeTitle: 'Host your docs on a subdirectory',
        upgradeDescription:
            'Publish your content on a subdirectory of your domain to keep your brand consistent.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesSearchInsights,
        title: 'Search insights',
        tooltipLabel:
            'See what your users are searching for on your site to help identify content gaps.',
        upgradeTitle: 'Detailed search analytics',
        upgradeDescription:
            'Understand what users are searching for to better cater to their needs.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesPageFeedback,
        title: 'User feedback',
        tooltipLabel:
            'Understand how useful your content is by allowing your users to rate each page.',
        upgradeTitle: 'Get user feedback easily',
        upgradeDescription:
            'Collect valuable insights from users to improve your content and services.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesPdfExport,
        title: 'PDF export',
        tooltipLabel:
            'Allow your site visitors to easily export pages of your content in PDF format.',
        upgradeTitle: 'Allow easy PDF site exports',
        upgradeDescription:
            'Let users export pages as PDFs with a click for offline access and distribution.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    //
    // Sharing & visibility
    //
    {
        key: api.SiteFeatureKey.SitesUnlistedVisibility,
        downgradeStrategy: 'unpublish',
        title: 'Hide from search engines',
        tooltipLabel:
            'Create a docs site that is not indexed by search engines. Ideal for publishing docs for things like beta programs or user testing.',
        upgradeTitle: 'Opt for unlisted visibility',
        upgradeDescription:
            'Choose to publish your site publicly, but not appear in search engine results to control access and privacy.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesShareLinks,
        downgradeStrategy: 'unpublish',
        title: 'Private share links',
        tooltipLabel:
            'Publish a docs site that is only accessible to people with a unique link, which can be revoked at any time.',
        upgradeTitle: 'Share content more privately',
        upgradeDescription:
            'Create unique links to your content to share with specific people, and revoke them at any time.',
        listedInPlanCards: false,

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesVisitorAuthentication,
        downgradeStrategy: 'unpublish',
        title: 'Authenticated access',
        tooltipLabel:
            'Use an authentication integration or server-side code to control which specific users have access your documentation.',
        upgradeTitle: 'Secure your content',
        upgradeDescription: 'Control access to your content with robust authentication options.',

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    //
    // Configuration
    //
    {
        key: api.SiteFeatureKey.SitesMultivariantSite,
        title: 'Site variants',
        tooltipLabel:
            'Publish multiple spaces on the same site to support localization and version releases for the same product.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesBasicCustomization,
        title: 'Basic customization',
        tooltipLabel:
            'Customize your site’s primary color and corner style, and enable a light/dark mode toggle.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAdvancedCustomization,
        title: 'Advanced customization',
        tooltipLabel:
            'Change your site’s logo, font, footer, and primary/header/header link colors to create highly branded docs.',

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesSections,
        downgradeStrategy: 'unpublish',
        title: 'Site sections',
        tooltipLabel:
            'Split your documentation into distinct sections or group the documentation of multiple products into a single site.',
        upgradeTitle: 'Get your docs in order',
        upgradeDescription:
            'Divide your site into sections to showcase every part of your product on a single docs site.',

        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: true,
    },

    //
    // Unlisted
    //
    {
        key: api.SiteFeatureKey.SitesPublicVisibility,
        title: 'Public visibility',
        tooltipLabel: 'Publish your docs site to everyone, and index it in search engines.',
        upgradeTitle: 'Maximize site visibility',
        upgradeDescription:
            'Ensure your site is visible to everyone, increasing your reach and impact.',
        listedInPlanCards: false,

        [SiteType.Basic]: true,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: true,
        [SiteType.Ultimate]: true,
    },

    {
        key: api.SiteFeatureKey.SitesAds,
        title: 'Site ads',
        tooltipLabel: 'Generate revenue with ads',
        upgradeTitle: 'Monetize your site',
        upgradeDescription:
            'Monetize your site by displaying ads to your visitors and earn revenue.',
        [SiteType.Basic]: false,
        [SiteType.Sponsored]: true,
        [SiteType.Premium]: false,
        [SiteType.Ultimate]: false,
    },
] as const;

// SITE_FEATURES_INPUT are untyped arrays so that SiteFeatureKey can be extracted as an union.
// We want to validate that SITE_FEATURES is a valid array of SiteFeatureDefinition.
export const SITE_FEATURES: readonly SiteFeatureDefinition[] = SITE_FEATURES_INPUT;

export const SITE_FEATURES_MAP = SITE_FEATURES.reduce(
    (acc, feature) => {
        acc[feature.key] = feature;
        return acc;
    },
    {} as {
        [key in SiteFeatureKey]: SiteFeatureDefinition;
    }
);

/**
 * Check whether a feature is included in a plan for new sites, defaults, and plan listings.
 */
export function isSiteFeatureIncludedInPlan(
    featureKey: SiteFeatureKey,
    siteType: SiteType
): boolean {
    const featureValue = SITE_FEATURES_MAP[featureKey][siteType];

    return featureValue !== false;
}

/**
 * Maps a list of api.SiteCustomizationFeature keys to a readable list of
 * advanced customization settings
 */
export function mapAdvancedCustomizationsToReadableLabels(
    advancedCustomizations: api.SiteCustomizationFeature[]
): string[] {
    const AdvancedCustomizationsLabels: Record<api.SiteCustomizationFeature, string> = {
        [api.SiteCustomizationFeature.HeaderLogo]: 'Header logo',
        [api.SiteCustomizationFeature.HeaderPrimaryLink]: 'Header primary link',
        [api.SiteCustomizationFeature.ThemePreset]: 'Theme preset',
        [api.SiteCustomizationFeature.PremiumFonts]: 'Premium fonts',
        [api.SiteCustomizationFeature.CustomIcons]: 'Custom icons',
        [api.SiteCustomizationFeature.FooterLogo]: 'Footer logo',
        [api.SiteCustomizationFeature.FooterLinks]: 'Footer links',
        [api.SiteCustomizationFeature.FooterCopyright]: 'Footer copyright',
        [api.SiteCustomizationFeature.SemanticColors]: 'Semantic colors',
    };

    return advancedCustomizations
        .map((customizationId) => AdvancedCustomizationsLabels[customizationId])
        .filter(filterOutNullable);
}

/**
 * Get the minimum site subscription required for a given feature.
 */
function getMinimumSiteSubscriptionForFeature(
    featureKey: SiteFeatureKey,
    defaultSiteType: SiteType
): SiteType {
    const allSiteSubscriptions = [
        isSiteFeatureIncludedInPlan(featureKey, SiteType.Basic) ? SiteType.Basic : null,
        isSiteFeatureIncludedInPlan(featureKey, SiteType.Sponsored) &&
        defaultSiteType === api.SiteType.Sponsored
            ? SiteType.Sponsored
            : null,
        isSiteFeatureIncludedInPlan(featureKey, SiteType.Premium) ? SiteType.Premium : null,
        isSiteFeatureIncludedInPlan(featureKey, SiteType.Ultimate) ? SiteType.Ultimate : null,
    ].filter(filterOutNullable);
    return allSiteSubscriptions[0];
}

/**
 * Get the minimum site subscription required for a list of features.
 * Returns null if the feature is not available on any plan.
 */
export function getMinimumSiteSubscriptionForFeatures(input: {
    featureKeys: SiteFeatureKey[];
    defaultSiteType: SiteType;
}): SiteType | null {
    const { featureKeys, defaultSiteType } = input;
    const siteProducts =
        defaultSiteType === SiteType.Sponsored
            ? SITE_PRICING_PRODUCTS
            : SITE_PRICING_PRODUCTS.filter((product) =>
                  product.targets.includes(SitePlanTarget.CUSTOMERS)
              );
    const siteTypes = siteProducts.map(({ key }) => key);
    return featureKeys.reduce<SiteType | null>((acc, key) => {
        const minimumSiteSubscription = getMinimumSiteSubscriptionForFeature(key, defaultSiteType);
        const accIndex = acc === null ? -1 : siteTypes.indexOf(acc);
        if (siteTypes.indexOf(minimumSiteSubscription) > accIndex) {
            return minimumSiteSubscription;
        }
        return acc;
    }, null);
}

// Returns unique features for any site type. Each array only includes features that the given site type unlocks, so any features from a lower type are *not* included.
export function getFeaturesPerSiteType() {
    return {
        [SiteType.Basic]: SITE_FEATURES_INPUT.filter((feature) =>
            isSiteFeatureIncludedInPlan(feature.key, SiteType.Basic)
        ),
        [SiteType.Sponsored]: SITE_FEATURES_INPUT.filter(
            (feature) =>
                isSiteFeatureIncludedInPlan(feature.key, SiteType.Sponsored) &&
                !isSiteFeatureIncludedInPlan(feature.key, SiteType.Basic)
        ),
        [SiteType.Premium]: SITE_FEATURES_INPUT.filter(
            (feature) =>
                isSiteFeatureIncludedInPlan(feature.key, SiteType.Premium) &&
                !isSiteFeatureIncludedInPlan(feature.key, SiteType.Basic)
        ),
        [SiteType.Ultimate]: SITE_FEATURES_INPUT.filter(
            (feature) =>
                isSiteFeatureIncludedInPlan(feature.key, SiteType.Ultimate) &&
                !isSiteFeatureIncludedInPlan(feature.key, SiteType.Premium) &&
                !isSiteFeatureIncludedInPlan(feature.key, SiteType.Basic)
        ),
    };
}
